import { FaShopify, FaPlus, FaCog } from 'react-icons/fa';
import { FiTrash2 } from 'react-icons/fi';
import { SiMeta } from 'react-icons/si';
import type { Connection } from '~/stores/connections';

type ConnectionsDropdownProps = {
  shopify?: Connection;
  shopifySyncStatus?: string;
  metaAds?: Connection;
  metaSyncStatus?: string;
  onAddShopify: () => void;
  onAddMeta: () => void;
  onDisconnectShopify?: () => void;
  onDisconnectMeta?: () => void;
};

export default function ConnectionsDropdown({
  shopify,
  shopifySyncStatus,
  metaAds,
  metaSyncStatus,
  onAddShopify,
  onAddMeta,
  onDisconnectShopify,
  onDisconnectMeta,
}: ConnectionsDropdownProps) {
  const getSyncStatusBadge = (status?: string) => {
    const badgeClasses: Record<string, string> = {
      COMPLETED: 'badge-success',
      IN_SYNC: 'badge-info',
      PENDING: 'badge-warning',
      FAILED: 'badge-error',
    };

    const badgeLabels: Record<string, string> = {
      COMPLETED: 'Active',
      IN_SYNC: 'Syncing',
      PENDING: 'Pending',
      FAILED: 'Failed',
    };

    const effectiveStatus = status || 'PENDING';
    return (
      <span
        className={`badge badge-sm flex-shrink-0 ${badgeClasses[effectiveStatus] || 'badge-warning'}`}
      >
        {badgeLabels[effectiveStatus] || 'Pending'}
      </span>
    );
  };

  return (
    <div className="dropdown dropdown-end">
      <button
        tabIndex={0}
        className="btn btn-ghost gap-2"
        aria-label="Manage connections"
      >
        <FaCog className="text-lg" />
        Connections
      </button>
      <ul
        tabIndex={0}
        className="dropdown-content menu bg-base-200 rounded-box z-[1] w-64 p-2 shadow-xl mt-2 right-0"
      >
        {/* Shopify Connection */}
        {shopify && (
          <li>
            <div className="flex items-center gap-2 py-2 px-1 cursor-default hover:bg-transparent">
              <FaShopify className="text-green-600 text-lg flex-shrink-0" />
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="font-medium text-sm truncate max-w-[100px]">
                  {shopify.name}
                </p>
                <p className="text-xs opacity-70">Shopify Store</p>
              </div>
              {getSyncStatusBadge(shopifySyncStatus)}
              {onDisconnectShopify && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDisconnectShopify();
                  }}
                  className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10"
                  title="Disconnect Shopify"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </li>
        )}

        {/* Meta Ads Connection */}
        {metaAds && (
          <li>
            <div className="flex items-center gap-2 py-2 px-1 cursor-default hover:bg-transparent">
              <SiMeta className="text-blue-600 text-lg flex-shrink-0" />
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="font-medium text-sm truncate max-w-[100px]">
                  {metaAds.name}
                </p>
                <p className="text-xs opacity-70">Meta Ads</p>
              </div>
              {getSyncStatusBadge(metaSyncStatus)}
              {onDisconnectMeta && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDisconnectMeta();
                  }}
                  className="btn btn-ghost btn-xs btn-square text-error hover:bg-error/10"
                  title="Disconnect Meta Ads"
                >
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </li>
        )}

        {/* Divider if both connections exist or if we have add options */}
        {(shopify || metaAds) && (!shopify || !metaAds) && (
          <li className="menu-title">
            <span className="divider my-1"></span>
          </li>
        )}

        {/* Add Meta Ads option */}
        {!metaAds && (
          <li>
            <button
              onClick={onAddMeta}
              className="flex items-center gap-3 py-3"
            >
              <FaPlus className="text-sm" />
              <span>Add Meta Ads</span>
            </button>
          </li>
        )}

        {/* Add Shopify option */}
        {!shopify && (
          <li>
            <button
              onClick={onAddShopify}
              className="flex items-center gap-3 py-3"
            >
              <FaPlus className="text-sm" />
              <span>Add Shopify</span>
            </button>
          </li>
        )}

        {/* Info text - only show when both exist and both are healthy */}
        {shopify &&
          metaAds &&
          shopifySyncStatus === 'COMPLETED' &&
          metaSyncStatus === 'COMPLETED' && (
            <li className="menu-title">
              <span className="text-xs opacity-60 px-4 py-2">
                All connections active
              </span>
            </li>
          )}
      </ul>
    </div>
  );
}
